import React, { createContext, useContext, useEffect, useState } from 'react';
import { getFeatureVisibility } from '../services/practiceContent';

// Admin-controlled feature visibility per class + subscription plan (docs/
// DECISIONS.md, "Admin-controlled feature visibility per class" /
// "Trial Plan: daily practice question quota" plan file, Part B) - lifted
// out of StudentSidebar.jsx (which used to fetch this itself) once Ask
// LEXA/Agent LEXA gating needed the same data in components that aren't the
// sidebar (QuestionPanel, StudentLayout's AgentLexaWidget). Fetched once per
// provider mount (StudentLayout.jsx, which wraps the whole logged-in
// session), not per consumer.
const FeatureVisibilityContext = createContext(null);

export function FeatureVisibilityProvider({ children }) {
  // `visibility` only ever lists explicitly disabled features - a key not
  // present means visible, matching getFeatureVisibility's own documented
  // "fail open" contract, so the empty initial state here renders every
  // item exactly like today, never a flash of a fully-empty menu.
  // `planAllowedFeatures` is Phase 2's subscription-plan restriction -
  // `null` (no active plan) means unrestricted by this layer; an array
  // means a feature must be in it, confirmed with the user as an AND
  // against the class toggle, not an override.
  const [featureVisibility, setFeatureVisibility] = useState({});
  const [planAllowedFeatures, setPlanAllowedFeatures] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getFeatureVisibility().then(({ visibility, plan_allowed_features }) => {
      if (cancelled) return;
      setFeatureVisibility(visibility || {});
      setPlanAllowedFeatures(plan_allowed_features || null);
    });
    return () => { cancelled = true; };
  }, []);

  const isVisible = (key) => featureVisibility[key] !== false && (planAllowedFeatures === null || planAllowedFeatures.includes(key));

  return (
    <FeatureVisibilityContext.Provider value={{ isVisible, featureVisibility, planAllowedFeatures }}>
      {children}
    </FeatureVisibilityContext.Provider>
  );
}

export function useFeatureVisibility() {
  const context = useContext(FeatureVisibilityContext);
  if (!context) {
    throw new Error('useFeatureVisibility must be used within FeatureVisibilityProvider');
  }
  return context;
}
